// This is the header Mega Menu Tastic file for displaying hierarchical categories (level 1, level 2, level 3).
// Users can navigate through these categories in both mobile and desktop views.
// The component shows all levels of categories based on the hierarchy.
// Data is sourced from Ampilience CMS.

'use client';

import AnnouncementBar from 'components/commercetools-ui/organisms/announcement-bar';
import Header from 'components/commercetools-ui/organisms/header';
import MaintenanceBar from 'components/commercetools-ui/organisms/maintenance-bar';
import { MarketProvider } from 'context/market';
import { useAccount, useCart, useProduct } from 'frontastic/hooks';
import { DataLayerHelpers } from 'helpers/dataLayerHelpers';
import useLocale from 'helpers/hooks/useLocale';
import useResizeObserver from 'helpers/hooks/useResizeObserver';
import { usePathname } from 'next/navigation';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { HeaderType } from 'types/next';

export type MenuItem = {
  find(arg0: (cat: any) => boolean): unknown;
  deliveryId: string;
  parentId: string;
  name: string;
  url: string | null;
  image: string | null;
  position: string;
  level2?: Level2Item[];
  ImageForMobile?: MobileImage;
  mobileImage?: string;
  priority?: number;
  AltTextForMobile?: string;
  AltTextForDesktopImage?: string;
  LinkForTheDesktopImage?: string;
  LinkForTheMobileImage?: string;
  backgroundGradientMobile?: string;
  backgroundGradientDesktop?: string;
  MobileBackgroundColour?: string;
  DesktopBackgroundColour?: string;
  MobileTextColor?: string;
  DeskTopTextColor?: string;
  HighLightedTextColour?: string;
};

export type MobileImage = {
  _meta: {
    schema: string;
  };
  id: string;
  name: string;
  endpoint: string;
  defaultHost: string;
  mimeType: string;
};

export type Level2Item = {
  deliveryId: string;
  parentId: string;
  name: string;
  url: string | null;
  image: string | null;
  position: string;
  level3?: Level3Item[];
  HideFromDesktop?: boolean;
  HideFromMobile?: boolean;
  DeskTopTextColor?: string;
  MobileTextColor?: string;
};

export type Level3Item = {
  deliveryId: string;
  parentId: string;
  name: string;
  url: string | null;
  image: string | null;
  position: string;
  DeskTopTextColor?: string;
  MobileTextColor?: string;
};

const HeaderTasticContentFul = (Headerdata: any) => {
  const { result } = useAccount();
  const { getProduct } = useProduct();
  const { locale } = useLocale();
  const pathname = usePathname();
  const [cookies, setCookie] = useCookies(['locale_selection', 'frontastic-session']);
  const { data } = useCart();
  const hasTriggered = useRef(false);
  const isFirstRender = useRef(true);
  const [contentFulHeader, setContentFulHeader] = useState(true);
  const headerRef = useRef<HTMLDivElement>(null);
  const cartContentPushedRef = useRef(false);
  const setPaddingTop = useCallback(() => {
    if (headerRef.current) {
      document.body.style.paddingTop = `${headerRef.current.clientHeight - 1}px`;
    }
  }, []);

  const removePaddingTop = useCallback(() => {
    document.body.style.paddingTop = '0px';
  }, []);

  useResizeObserver(headerRef, setPaddingTop, removePaddingTop);

  const announcementBarData = {
    text: Headerdata?.data?.text,
    highlightedSubstring: Headerdata?.data?.highlightedSubstring,
    target: Headerdata?.data?.target,
  };

  const maintenanceData = {
    activateMaintenance: Headerdata?.data?.activateMaintenance,
    maintenanceText: Headerdata?.data?.maintenanceText,
  };

  const mapper = Headerdata?.data?.data?.dataSource;

  function sortMenu(menu: any) {
    if (menu?.length > 0) {
      menu?.sort((a: any, b: any) => a?.priority - b?.priority);

      return menu?.map((category: any) => {
        category.level2 = category?.level2?.sort((a: any, b: any) => a?.priority - b?.priority);

        category?.level2?.forEach((subCategory: any) => {
          if (!Array.isArray(subCategory?.level3)) {
            subCategory.level3 = [];
          }
          subCategory?.level3?.sort((a: any, b: any) => a?.priority - b?.priority);
        });

        return category;
      });
    }
    return null;
  }

  const sortedMenu = sortMenu(mapper);

  const isloggedIn = result?.data?.data?.loggedIn;

  const userData = {
    event: 'userData',
    currencyCode: cookies?.locale_selection?.currency,
    country: cookies?.locale_selection?.countryCode,
    userType: isloggedIn ? 'Registered' : 'Guest',
    brandstakId: isloggedIn ? result?.data?.data?.account?.externalId : '',
    userEmail: isloggedIn ? result?.data?.data?.account?.email : '',
    dimension1: isloggedIn ? result?.data?.data?.account?.accountId : '',
    dimension2: isloggedIn ? 'login-yes' : 'login-no',
    dimension3: 'loyalty-no',
    dimension13: isloggedIn ? result?.data?.data?.account?.title : '',
  };

  useEffect(() => {
    if (hasTriggered.current || typeof window === 'undefined') return;
    hasTriggered.current = true;

    const dataLayerCart = new DataLayerHelpers(pathname, locale);
    dataLayerCart.pushUserData(userData);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const dataLayerCart = new DataLayerHelpers(pathname, locale);
    const cartId = data?.cartId ?? '';
    const lineItems = Array.isArray(data?.lineItems) ? data?.lineItems : [];

    // Push actual items if present
    if (cartId && Array.isArray(lineItems) && lineItems?.length > 0) {
      dataLayerCart.pushProductCartContent(lineItems);
      return;
    }

    // Fallback after 5s if cart still has no items
    const timeout = setTimeout(() => {
      const delayedLineItems = Array.isArray(data?.lineItems) ? data?.lineItems : [];
      const delayedCartId = data?.cartId ?? '';

      if (!delayedCartId || delayedLineItems?.length === 0) {
        dataLayerCart.pushProductCartContent([]);
      }
    }, 1000);

    return () => clearTimeout(timeout);
  }, [data?.cartId, JSON.stringify(data?.lineItems)]);

  return (
    <MarketProvider>
      <div id="header-container" className="fixed top-0 z-50 w-full" ref={headerRef}>
        <AnnouncementBar {...announcementBarData} />
        {maintenanceData.activateMaintenance && <MaintenanceBar {...maintenanceData} />}
        <Header
          categories={Headerdata?.categories}
          logo={Headerdata?.data?.logo}
          logoLink={Headerdata?.data?.logoLink}
          logoMobile={Headerdata?.data?.logoMobile}
          logoLinkMobile={Headerdata?.data?.logoLinkMobile}
          tiles={Headerdata?.data?.tiles}
          emptyCartTitle={Headerdata?.data?.emptyCartTitle}
          emptyCartSubtitle={Headerdata?.data?.emptyCartSubtitle}
          emptyCartImage={Headerdata?.data?.emptyCartImage}
          emptyCartCategories={Headerdata?.data?.emptyCartCategories}
          emptyWishlistTitle={Headerdata?.data?.emptyWishlistTitle}
          emptyWishlistSubtitle={Headerdata?.data?.emptyWishlistSubtitle}
          emptyWishlistImage={Headerdata?.data?.emptyWishlistImage}
          emptyWishlistCategories={Headerdata?.data?.emptyWishlistCategories}
          enableAlgoliaSearch={Headerdata?.data?.enableAlgoliaSearch}
          navLinks={Headerdata?.categories?.filter((category: any) => category?.depth === 0)}
          menu={sortedMenu}
          contentFulHeader={contentFulHeader}
          searchConfiguration={Headerdata?.data?.searchDataConfiguration}
        />
      </div>
    </MarketProvider>
  );
};

export default HeaderTasticContentFul;
